import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;
const ROD_RADIUS = 0.5 * IN;
const FINIAL_RADIUS = 0.75 * IN;
const BRACKET_INSET = 3 * IN;
const RING_OUTER_R = 0.6875 * IN;
const PLATE_W = 1.25 * IN;
const PLATE_H = 2 * IN;
const PLATE_D = 0.125 * IN;
const ARM_THICK = 0.3125 * IN;

export function CurtainRodSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <SideView
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <FrontView
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function FrontView({ width, height, strokeColor, strokeWidth }) {
  const cy = height / 2;
  const halfW = width / 2;
  const endInset = Math.min(BRACKET_INSET, halfW - FINIAL_RADIUS * 2);
  const leftBk = endInset;
  const rightBk = width - endInset;

  return (
    <g>
      {/* Main rod */}
      <Line
        x1={FINIAL_RADIUS}
        y1={cy}
        x2={width - FINIAL_RADIUS}
        y2={cy}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Ball finials */}
      {[FINIAL_RADIUS, width - FINIAL_RADIUS].map((cx) => (
        <circle
          key={`finial-${cx}`}
          cx={cx}
          cy={cy}
          r={FINIAL_RADIUS}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Bracket rings */}
      {[leftBk, rightBk].map((cx) => (
        <circle
          key={`ring-${cx}`}
          cx={cx}
          cy={cy}
          r={RING_OUTER_R}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Bracket arms (short lines below rings) */}
      {[leftBk, rightBk].map((cx) => (
        <Line
          key={`arm-${cx}`}
          x1={cx}
          y1={cy + RING_OUTER_R}
          x2={cx}
          y2={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

function SideView({ depth, height, strokeColor, strokeWidth }) {
  const cy = height / 2;
  const rodX = depth - FINIAL_RADIUS;

  return (
    <g>
      {/* Wall plate */}
      <rect
        x={0}
        y={cy - PLATE_H / 2}
        width={PLATE_D}
        height={PLATE_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Arm from plate to rod */}
      <Line
        x1={PLATE_D}
        y1={cy}
        x2={rodX}
        y2={cy}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Rod cross-section */}
      <circle
        cx={rodX}
        cy={cy}
        r={ROD_RADIUS}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Ring around rod */}
      <circle
        cx={rodX}
        cy={cy}
        r={RING_OUTER_R}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

CurtainRodSchematic.supportsSideViews = true;
